// components/Pages/RM/Inventory/ThresholdModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';

const ThresholdModal = ({ show, onHide, inventoryItem, onSave }) => {
    const [formData, setFormData] = useState({
        minStock: 0,
        maxStock: '',
        reorderPoint: 10,
        reorderQuantity: 50
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (inventoryItem) {
            setFormData({
                minStock: inventoryItem.thresholds?.minStock || 0,
                maxStock: inventoryItem.thresholds?.maxStock || '',
                reorderPoint: inventoryItem.thresholds?.reorderPoint || 10,
                reorderQuantity: inventoryItem.thresholds?.reorderQuantity || 50
            });
        }
    }, [inventoryItem]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (formData.minStock < 0) {
            newErrors.minStock = 'Minimum stock cannot be negative';
        }

        if (formData.reorderPoint < 0) {
            newErrors.reorderPoint = 'Reorder point cannot be negative';
        }

        if (formData.reorderQuantity < 0) {
            newErrors.reorderQuantity = 'Reorder quantity cannot be negative';
        }

        if (formData.maxStock && formData.maxStock < 0) {
            newErrors.maxStock = 'Maximum stock cannot be negative';
        }

        if (formData.maxStock && formData.maxStock <= formData.minStock) {
            newErrors.maxStock = 'Maximum stock must be greater than minimum stock';
        }

        if (formData.reorderPoint < formData.minStock) {
            newErrors.reorderPoint = 'Reorder point should be greater than minimum stock';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            const data = {
                minStock: parseFloat(formData.minStock),
                maxStock: formData.maxStock ? parseFloat(formData.maxStock) : null,
                reorderPoint: parseFloat(formData.reorderPoint),
                reorderQuantity: parseFloat(formData.reorderQuantity)
            };
            onSave(data);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Update Thresholds - {inventoryItem?.rawMaterial?.name}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Alert variant="info">
                        <strong>Current Stock:</strong> {inventoryItem?.currentStock?.quantity} {inventoryItem?.currentStock?.unit}
                    </Alert>

                    <Form.Group className="mb-3">
                        <Form.Label>Minimum Stock *</Form.Label>
                        <Form.Control
                            type="number"
                            name="minStock"
                            value={formData.minStock}
                            onChange={handleChange}
                            required
                            min="0"
                            step="0.01"
                            isInvalid={!!errors.minStock}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.minStock}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            The minimum quantity that should always be in stock
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Maximum Stock</Form.Label>
                        <Form.Control
                            type="number"
                            name="maxStock"
                            value={formData.maxStock}
                            onChange={handleChange}
                            min="0"
                            step="0.01"
                            isInvalid={!!errors.maxStock}
                            placeholder="Leave empty for unlimited"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.maxStock}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            The maximum quantity to keep in stock (optional)
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Reorder Point *</Form.Label>
                        <Form.Control
                            type="number"
                            name="reorderPoint"
                            value={formData.reorderPoint}
                            onChange={handleChange}
                            required
                            min="0"
                            step="0.01"
                            isInvalid={!!errors.reorderPoint}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.reorderPoint}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            When stock reaches this level, a reorder is triggered
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Reorder Quantity *</Form.Label>
                        <Form.Control
                            type="number"
                            name="reorderQuantity"
                            value={formData.reorderQuantity}
                            onChange={handleChange}
                            required
                            min="0"
                            step="0.01"
                            isInvalid={!!errors.reorderQuantity}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.reorderQuantity}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            The quantity to order when reorder point is reached
                        </Form.Text>
                    </Form.Group>

                    <Alert variant="warning" className="mt-3">
                        <strong>Recommended:</strong> Reorder point should be enough to cover demand during lead time.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary">
                        Save Thresholds
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ThresholdModal;